import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Star, Download, ShieldCheck, Share2, Heart, Flag, QrCode, ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, Coins, Play, Image as ImageIcon,
} from "lucide-react";
import { fetchApp, formatDownloads, trackLaunch, streakCheckin } from "@/lib/api";
import ReviewsSection from "@/components/ReviewsSection";
import { AppCard } from "@/components/AppCard";
import Footer from "@/components/Footer";
import TipModal from "@/components/TipModal";
import FollowButton from "@/components/FollowButton";
import CommentsSection from "@/components/CommentsSection";
import ShareCardModal from "@/components/ShareCardModal";
import PwaLauncher from "@/components/PwaLauncher";
import { RankBadges } from "@/components/RankBadges";
import { useAuth } from "@/contexts/AuthContext";
import { APP_DETAIL, SHARE } from "@/constants/testIds";

export default function AppDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [shot, setShot] = useState(0);
  const [tab, setTab] = useState("about");
  const [qrOpen, setQrOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [reviewCount, setReviewCount] = useState(null);

  useEffect(() => {
    setData(null);
    setReviewCount(null);
    fetchApp(slug).then(setData).catch(() => setData({ error: true }));
  }, [slug]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 mono text-[#00F0FF] animate-pulse">Loading…</div>;
  if (data.error) return <div className="max-w-7xl mx-auto px-6 py-20">App not found. <Link to="/browse" className="text-[#00F0FF]">Browse all</Link></div>;

  const { app, similar } = data;

  const share = () => {
    if (navigator.share) navigator.share({ title: app.name, url: window.location.href }).catch(() => {});
    else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  // First-party Jixxeo apps open in place (their own single header), not inside the framed launcher
  const ownApp = (u) => /^\/(?!embed\/|g\/)[a-z]/.test(u || "");
  const openOwn = () => { trackLaunch(app.slug); if (user) streakCheckin().catch(() => {}); nav(app.launch_url); };

  const install = () => {
    if (ownApp(app.launch_url)) return openOwn();
    if (app.is_pwa && app.launch_url) {
      setLaunchOpen(true);
      toast.success(`Launching ${app.name}…`, { description: "Playing right here in Jixxeo" });
      return;
    }
    trackLaunch(app.slug);
    if (app.launch_url && /^https?:/.test(app.launch_url)) {
      window.open(user ? `/api/sso/handoff?target=${encodeURIComponent(app.launch_url)}` : app.launch_url, "_blank", "noopener");
      return;
    }
    setQrOpen(true);
    toast.success(`Preparing ${app.name} v${app.version}…`, { description: "Scan QR to install on phone." });
  };

  const launchPwa = () => {
    if (!app.launch_url) return;
    if (ownApp(app.launch_url)) return openOwn();
    setLaunchOpen(true);
    toast.success("Launching…");
  };

  const openTip = () => {
    if (!user) { nav("/login"); return; }
    setTipOpen(true);
  };

  return (
    <div data-testid={APP_DETAIL.root}>
      {/* Banner */}
      <div className="relative">
        <div className="h-64 md:h-80 relative overflow-hidden">
          <img src={app.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/70 to-[#050505]" />
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <Link to="/browse" className="absolute top-6 left-6 md:relative md:top-auto md:left-auto md:mt-0 mono text-[10px] uppercase tracking-widest text-white/60 hover:text-[#00F0FF] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>

          {/* App header */}
          <div className="relative -mt-24 md:-mt-28 flex flex-col md:flex-row gap-6 items-start">
            <img
              src={app.icon_url}
              alt=""
              className="w-28 h-28 md:w-40 md:h-40 rounded-3xl ring-4 ring-[#050505] object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-1">
                // {app.category} · v{app.version}
              </div>
              <h1 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-none">
                {app.name}
              </h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-white/70">{app.developer}</span>
                {app.developer_verified && (
                  <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-0.5 rounded-full border border-[#00F0FF]/30">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
                <FollowButton developerSlug={app.developer_slug} developerName={app.developer} size="sm" />
              </div>
              <p className="text-white/60 mt-3 max-w-2xl">{app.tagline}</p>
              <RankBadges slug={app.slug} />

              {/* Meta strip */}
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
                <MetaTile label="Rating" value={app.ratings_count ? app.rating.toFixed(1) : "—"} sub={`${formatDownloads(app.ratings_count)} reviews`} icon={<Star className="w-3.5 h-3.5 fill-[#FFE300] text-[#FFE300]" />} />
                <MetaTile label="Launches" value={formatDownloads(app.downloads)} sub="tracked" icon={<Play className="w-3.5 h-3.5" />} />
                <MetaTile label="Size" value={`${app.file_size_mb} MB`} sub="APK" />
                <MetaTile label="Price" value={app.is_free ? "Free" : `$${app.price}`} sub={app.is_open_source ? "Open Source" : ""} />
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-6">
                <button
                  data-testid={APP_DETAIL.downloadBtn}
                  onClick={install}
                  className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full transition-colors pulse-neon"
                >
                  <Play className="w-4 h-4" />
                  {app.is_pwa ? "Launch Instant" : (app.is_free ? "Launch" : `Buy · $${app.price}`)}
                </button>
                {app.is_pwa && (
                  <button
                    data-testid={APP_DETAIL.launchPwaBtn}
                    onClick={launchPwa}
                    className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/20 mono text-xs uppercase tracking-widest px-4 py-3 rounded-full transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" /> Play Now
                  </button>
                )}
                <button
                  data-testid={APP_DETAIL.qrToggle}
                  onClick={() => setQrOpen(true)}
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00F0FF]/60 hover:text-[#00F0FF] text-white/80 mono text-xs uppercase tracking-widest px-5 py-3 rounded-full transition-colors"
                >
                  <QrCode className="w-4 h-4" /> Scan to Phone
                </button>
                <button
                  data-testid={APP_DETAIL.tipBtn}
                  onClick={openTip}
                  className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-4 py-3 rounded-full border border-[#FFE300]/40 bg-[#FFE300]/10 text-[#FFE300] hover:bg-[#FFE300]/20 transition-colors"
                >
                  <Coins className="w-4 h-4" /> Tip Dev
                </button>
                <button data-testid={SHARE.cardBtn} onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-4 py-3 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14] hover:bg-[#39FF14]/20 transition-colors">
                  <ImageIcon className="w-4 h-4" /> Share Card
                </button>
                <IconBtn onClick={share} icon={<Share2 className="w-4 h-4" />} label="Share" />
                <IconBtn icon={<Heart className="w-4 h-4" />} label="Save" onClick={() => toast.success("Saved to wishlist")} />
                <IconBtn icon={<Flag className="w-4 h-4" />} label="Report" onClick={() => toast("Thanks — moderation team notified")} />
              </div>

              {app.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {app.tags.map((t) => (
                    <span key={t} className="mono text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-3 py-1 rounded-full text-white/70">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Screenshots */}
          <section data-testid={APP_DETAIL.screenshots} className="mt-12">
            <SectionTitle>Screenshots</SectionTitle>
            <div className="relative">
              <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 relative">
                <img src={app.screenshots[shot]} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <button
                  onClick={() => setShot((shot - 1 + app.screenshots.length) % app.screenshots.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass-strong flex items-center justify-center hover:text-[#00F0FF]"
                >
                  <ChevronLeft />
                </button>
                <button
                  onClick={() => setShot((shot + 1) % app.screenshots.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass-strong flex items-center justify-center hover:text-[#00F0FF]"
                >
                  <ChevronRight />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {app.screenshots.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setShot(i)}
                      className={`h-1.5 rounded-full transition-all ${i === shot ? "w-8 bg-[#00F0FF]" : "w-1.5 bg-white/40"}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-3 overflow-x-auto hide-scrollbar">
                {app.screenshots.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setShot(i)}
                    className={`w-32 aspect-video rounded-lg overflow-hidden border flex-shrink-0 transition-colors ${
                      i === shot ? "border-[#00F0FF]" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img src={s} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section className="mt-14">
            <div className="flex gap-1 border-b border-white/10">
              {[
                { id: "about", label: "About" },
                { id: "reviews", label: `Reviews · ${reviewCount ?? (app.ratings_count || 0)}` },
                { id: "changelog", label: "Changelog" },
                { id: "permissions", label: "Permissions" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`app-detail-tab-${t.id}`}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-3 transition-colors relative ${
                    tab === t.id ? "text-[#00F0FF]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {t.label}
                  {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#00F0FF]" />}
                </button>
              ))}
            </div>

            <div className="py-8">
              {tab === "about" && (
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-2 space-y-4">
                    <p className="text-white/70 leading-relaxed">{app.description}</p>
                  </div>
                  <div className="glass rounded-2xl p-5 space-y-3 text-sm h-fit">
                    <InfoRow k="Version" v={app.version} />
                    <InfoRow k="Released" v={app.released_at} />
                    <InfoRow k="Updated" v={app.updated_at} />
                    <InfoRow k="File size" v={`${app.file_size_mb} MB`} />
                    <InfoRow k="Category" v={app.category} />
                    <InfoRow k="License" v={app.is_open_source ? "Open Source" : "Proprietary"} />
                    <a href="#" className="mono text-xs text-[#00F0FF] inline-flex items-center gap-1 pt-2">
                      Privacy policy <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}

              {tab === "reviews" && <ReviewsSection slug={app.slug} testId={APP_DETAIL.reviews} onCount={setReviewCount} />}

              {tab === "changelog" && (
                <div className="space-y-4">
                  {app.changelog.map((c, i) => (
                    <div key={i} className="glass rounded-2xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="mono text-xs text-[#00F0FF]">v{c.version}</div>
                        <div className="mono text-[10px] text-white/40">{c.date}</div>
                      </div>
                      <p className="text-white/70 text-sm mt-2">{c.notes}</p>
                    </div>
                  ))}
                </div>
              )}

              {tab === "permissions" && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {app.permissions.map((p) => (
                    <div key={p} className="glass rounded-xl p-4 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4 text-[#39FF14]" />
                      </div>
                      <div>
                        <div className="font-bold text-sm">{p}</div>
                        <div className="mono text-[10px] text-white/40">granted at install</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Similar */}
          {similar?.length > 0 && (
            <section data-testid={APP_DETAIL.similar} className="mt-14 mb-6">
              <SectionTitle>You might also like</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {similar.slice(0, 6).map((a) => (
                  <AppCard key={a.slug} app={a} />
                ))}
              </div>
            </section>
          )}

          <div id="comments" />
          <CommentsSection slug={slug} />
        </div>
      </div>

      {/* QR Modal */}
      {qrOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-black/70 backdrop-blur-sm" onClick={() => setQrOpen(false)}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="glass-strong rounded-3xl p-8 max-w-sm w-full text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mono text-[10px] uppercase tracking-widest text-[#00F0FF] mb-2">// scan to phone</div>
            <h3 className="font-heading font-black text-2xl uppercase">{app.name}</h3>
            <p className="text-white/60 text-sm mt-1">v{app.version} · {app.file_size_mb} MB</p>
            <div className="mt-6 mx-auto w-56 h-56 rounded-2xl bg-white p-3 glow-cyan">
              <QRSvg text={window.location.href} />
            </div>
            <p className="mono text-[10px] text-white/50 uppercase tracking-widest mt-4">
              Point your phone camera at the QR to install
            </p>
            <button onClick={() => setQrOpen(false)} className="mt-6 mono text-xs uppercase tracking-widest text-white/60 hover:text-white">
              Close
            </button>
          </motion.div>
        </div>
      )}

      {tipOpen && <TipModal open={tipOpen} onClose={() => setTipOpen(false)} app={app} />}
      {shareOpen && <ShareCardModal open={shareOpen} onClose={() => setShareOpen(false)} app={app} />}
      {launchOpen && <PwaLauncher open={launchOpen} onClose={() => setLaunchOpen(false)} app={app} />}

      <Footer />
    </div>
  );
}

function MetaTile({ label, value, sub, icon }) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="mono text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-heading font-black text-lg mt-1 flex items-center gap-1">
        {icon}{value}
      </div>
      {sub && <div className="mono text-[9px] text-white/40">{sub}</div>}
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-white/50 mono text-xs uppercase tracking-widest">{k}</span>
      <span className="text-white/90 mono text-xs">{v}</span>
    </div>
  );
}

function IconBtn({ icon, label, onClick }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 text-white/60 hover:text-white mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 hover:border-white/30 transition-colors">
      {icon} {label}
    </button>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-5">{children}</h2>
  );
}

// Simple deterministic pseudo-QR (visual placeholder). Real QR generation is Phase 2.
function QRSvg({ text }) {
  const size = 25;
  // deterministic hash
  let seed = 0;
  for (let i = 0; i < text.length; i++) seed = (seed * 31 + text.charCodeAt(i)) >>> 0;
  const rand = (x, y) => {
    const v = Math.sin(seed + x * 91.3 + y * 47.7) * 43758.5453;
    return v - Math.floor(v);
  };
  const cells = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (rand(x, y) > 0.55) cells.push([x, y]);
    }
  }
  // Finder patterns (corner squares)
  const corners = [[0, 0], [size - 7, 0], [0, size - 7]];
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      <rect width={size} height={size} fill="#fff" />
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#050505" />
      ))}
      {corners.map(([cx, cy], i) => (
        <g key={i}>
          <rect x={cx} y={cy} width="7" height="7" fill="#050505" />
          <rect x={cx + 1} y={cy + 1} width="5" height="5" fill="#fff" />
          <rect x={cx + 2} y={cy + 2} width="3" height="3" fill="#050505" />
        </g>
      ))}
    </svg>
  );
}
